// Service-call lifecycle (NOT-560) — D2 dual-read window.
//
// `Job.scopeMode` is the canonical workflow field. During the rolling-deploy
// window, older writers may only stamp `isServiceCall` or the legacy
// `source === 'SERVICE_CALL'`, so readers accept any diagnostic signal.

/** The minimal job shape the dual-read needs (Job, JobSummary, raw rows). */
interface ServiceCallJobLike {
  scopeMode?: 'DEFINED' | 'DIAGNOSTIC' | null;
  isServiceCall?: boolean | null;
  source?: string | null;
}

interface EstimateEligibleJobLike extends ServiceCallJobLike {
  estimateId?: string | null;
  bookingType?: string | null;
  status?: string | null;
  statusPhase?: string | null;
}

const ESTIMATE_ELIGIBLE_JOB_PHASES = new Set([
  'DRAFT',
  'SCHEDULED',
  'IN_PROGRESS',
  'ON_HOLD',
  'COMPLETED',
]);

export function isDiagnosticJob(job: ServiceCallJobLike | null | undefined): boolean {
  if (!job) return false;
  // Same OR as the backend's service-call.util — a row written by an old pod
  // during the rolling deploy (isServiceCall=false default + legacy source)
  // must classify identically on both sides.
  return (
    job.scopeMode === 'DIAGNOSTIC' || job.isServiceCall === true || job.source === 'SERVICE_CALL'
  );
}

/** @deprecated Compatibility name for existing service-call-labelled screens. */
export const isServiceCallJob = isDiagnosticJob;

/**
 * Human-readable kind for a Job record.
 *
 * Diagnostic scope is not a synonym for every one-off job. Keep this copy
 * decision beside the canonical dual-read so screens do
 * not infer the kind from booking type or from the presence of a visit.
 */
export function jobKindLabel(job: ServiceCallJobLike | null | undefined): 'Diagnostic job' | 'Job' {
  return isDiagnosticJob(job) ? 'Diagnostic job' : 'Job';
}

/**
 * Shared UI gate for creating an estimate from a job workspace.
 *
 * Every one-off Job can fan out to estimates. Their explicit fulfillment mode
 * decides whether sold scope returns to this Job or creates new work.
 */
export function canCreateEstimateFromJob(
  job: EstimateEligibleJobLike | null | undefined,
  options: { canCreateEstimates: boolean; pricingHidden: boolean; readOnly: boolean },
): boolean {
  if (!job || !options.canCreateEstimates || options.pricingHidden || options.readOnly)
    return false;
  if (job.bookingType === 'RECURRING') return false;

  const phase = job.statusPhase ?? job.status ?? '';
  if (!ESTIMATE_ELIGIBLE_JOB_PHASES.has(phase)) return false;

  return true;
}

/**
 * Search params for "Create estimate" on a job.
 *
 * Quoting new work off a job starts where every other estimate starts — the
 * manual/template picker at /dashboard/estimates/new — with nothing written
 * until the operator saves. The job's own lines are deliberately NOT copied:
 * the estimate is for work the job does not already cover, so a prefilled copy
 * of its scope was never the right starting point.
 *
 * `sourceJobId` is the only thing that travels (plus the customer, so the form
 * opens on the right one). It is what re-establishes the job → estimate link
 * on save; see EstimatesService.create.
 */
export function estimateFromJobSearch(
  job: { id: string; contactId?: string | null },
  options: {
    /**
     * The visit the operator is on. Travels to the create payload as
     * `originAppointmentId`, so the estimate is attributed to THAT visit's
     * technician (attributionQuality EXACT_VISIT) rather than to whichever
     * appointment the server finds first — the guess is wrong for exactly
     * the tech quoting from a second or later visit.
     */
    originAppointmentId?: string;
  } = {},
): {
  sourceJobId: string;
  fulfillmentMode: 'UNDECIDED';
  contactId?: string;
  originAppointmentId?: string;
} {
  return {
    sourceJobId: job.id,
    fulfillmentMode: 'UNDECIDED',
    ...(job.contactId ? { contactId: job.contactId } : {}),
    ...(options.originAppointmentId ? { originAppointmentId: options.originAppointmentId } : {}),
  };
}

/**
 * Search params for "Create proposal" on a job.
 *
 * The estimate twin above, minus the visit. Quoting a good/better/best proposal
 * off a job starts where every other proposal starts — the business-unit /
 * blank / template picker at /dashboard/proposals/new.
 *
 * The absence of `template` is the whole contract: NewProposalWrapper branches
 * on that key before anything else, so a hard-coded `template: 'scratch'` here
 * is what used to skip the picker and drop the operator straight into the
 * builder. `sourceJobId` is what re-establishes the job → proposal link.
 *
 * `originAppointmentId` is deliberately absent, unlike the estimate: the
 * proposal route's validateSearch does not read it, so sending one from the
 * visit cockpit would be a silent no-op rather than the EXACT_VISIT
 * attribution it implies.
 */
export function proposalFromJobSearch(job: { id: string; contactId?: string | null }): {
  sourceJobId: string;
  contactId?: string;
} {
  return {
    sourceJobId: job.id,
    ...(job.contactId ? { contactId: job.contactId } : {}),
  };
}
